// Client-side behavior for the signup surface.
//
// Tier-1 SSR (docs/ui-framework-strategy/rendering-strategy.md): no framework and
// no jQuery. Everything here is the doc's "purely local DOM behavior with no
// server involved" case -- disclosure toggles, conditional field reveals,
// mirroring one field into another -- which stays small vanilla handlers.
//
// Handlers are delegated from `document` so markup rendered after load still
// works, and every behavior is opted into by a data attribute rather than by
// class name, so a styling pass can rename classes freely.

import { syncConcealedControls } from "../concealed_controls.js";
// The country-aware address section on step 3. Self-contained: it registers
// its own handlers when imported.
import "../address_fields.js";

const TOAST_AUTO_DISMISS_DELAY = 5000;
const TOAST_ANIMATION_DURATION = 300;

/* ------------------------------------------------------------------ toasts */

const dismissToast = (toast) => {
  if (!toast || toast.dataset.dismissing === "true") return;
  toast.dataset.dismissing = "true";
  toast.classList.remove("toast-visible");

  setTimeout(() => {
    toast.remove();
    const container = document.querySelector("[data-toast-container]");
    if (container && container.querySelectorAll("[data-toast]").length === 0) {
      container.remove();
    }
  }, TOAST_ANIMATION_DURATION);
};

const initToasts = () => {
  document.querySelectorAll("[data-toast]").forEach((toast, index) => {
    // Stagger so multiple toasts read as a stack arriving, not a single jump.
    setTimeout(() => toast.classList.add("toast-visible"), index * 100);

    if (toast.dataset.autoDismiss === "true") {
      setTimeout(() => dismissToast(toast), TOAST_AUTO_DISMISS_DELAY + index * 100);
    }
  });
};

/* -------------------------------------------------------------- disclosure */

// `data-disclosure-for="<id>"` on a trigger shows/hides the element with that id.
const toggleDisclosure = (trigger) => {
  const target = document.getElementById(trigger.dataset.disclosureFor);
  if (!target) return;

  const nowHidden = !target.hidden;
  target.hidden = nowHidden;
  trigger.setAttribute("aria-expanded", String(!nowHidden));
};

/* ------------------------------------------------------------ field mirror */

// `data-mirror-fields` holds {"sourceId": "targetId"} pairs, applied when the
// element it sits on is checked. Used by "I'm the Billing Contact", which copies
// the admin's name and email into the billing fields.
const applyFieldMirror = (control) => {
  if (!control.checked) return;

  let pairs;
  try {
    pairs = JSON.parse(control.dataset.mirrorFields);
  } catch (e) {
    return;
  }

  Object.entries(pairs).forEach(([sourceId, targetId]) => {
    const target = document.getElementById(targetId);
    if (!target) return;

    // A source may be several ids joined by "+", so "first + last" can fill one
    // target field.
    target.value = sourceId
      .split("+")
      .map((id) => document.getElementById(id.trim()))
      .filter(Boolean)
      .map((el) => el.value)
      .join(" ")
      .trim();
  });
};

/* ----------------------------------------------------------- display toggle */

// Conditional field reveals: a control declares which parts of the form it
// governs, and they appear or disappear as its value changes.
//
//   data-toggle-selector       one CSS selector, or a JSON array of them
//   data-toggle-value-pattern  JSON array of regex sources, paired by index
//                              with the selector array
//
// A checkbox needs no pattern -- it reveals its targets when checked. A select
// or radio group matches its value against each pattern and reveals the
// selector paired with it.
//
// Visibility is the `hidden` attribute rather than a class, so it needs no CSS
// and correctly drops the element from the accessibility tree.

const parseMaybeJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
};

// Concealing a container also disables its `data-disable-when-hidden`
// controls, so a concealed copy of a duplicated field neither submits nor
// blocks validation. See concealed_controls.js.
const conceal = (el, hidden) => {
  el.hidden = hidden;
  syncConcealedControls(el);
};

const setHidden = (selector, hidden) => {
  document.querySelectorAll(selector).forEach((el) => {
    conceal(el, hidden);
  });
};

const updateDisplayToggle = (control) => {
  const selector = parseMaybeJson(control.dataset.toggleSelector);
  if (!selector) return;

  // Only the checked radio in a group carries an opinion.
  if (control.type === "radio" && !control.checked) return;

  if (control.type === "checkbox") {
    const selectors = Array.isArray(selector) ? selector : [selector];
    selectors.forEach((sel) => setHidden(sel, !control.checked));
    return;
  }

  const patterns = parseMaybeJson(control.dataset.toggleValuePattern);
  if (!patterns) {
    setHidden(Array.isArray(selector) ? selector[0] : selector, !control.value);
    return;
  }

  const patternList = Array.isArray(patterns) ? patterns : [patterns];
  const selectorList = Array.isArray(selector) ? selector : [selector];

  // Resolve every pattern first, then hide. Two patterns can name the same
  // element, and a later non-match must not undo an earlier match.
  const shown = new Set();
  const candidates = new Set();

  patternList.forEach((pattern, index) => {
    const sel = selectorList[index] !== undefined ? selectorList[index] : selectorList[0];
    const matches = new RegExp(pattern).test(control.value);

    document.querySelectorAll(sel).forEach((el) => {
      candidates.add(el);
      if (matches) shown.add(el);
    });
  });

  candidates.forEach((el) => {
    conceal(el, !shown.has(el));
  });
};

const initDisplayToggles = () => {
  document.querySelectorAll("[data-toggle-selector]").forEach(updateDisplayToggle);

  // Run over everything as well, not just what the toggles above touched. A
  // radio group with nothing checked yet never reaches its targets, so a
  // container the server rendered hidden would keep its controls enabled.
  syncConcealedControls();
};

/* ------------------------------------------------------------ input filters */

// Character filtering and auto-casing for the identifier fields. Length is
// enforced by the `maxlength` attribute the inputs already carry, so only the
// character set and casing are handled here.
//
// These are constraints the server also enforces -- this is about not letting
// someone type a subdomain in capitals and be corrected after submitting.
const INPUT_FILTERS = {
  // Subdomains become part of a URL: letters, digits and dashes, lowercased.
  subdomain: { allowed: /[^-A-Za-z0-9]/g, casing: "lower" },
  // Team abbreviations and club codes: alphanumeric, uppercased.
  abbreviation: { allowed: /[^A-Za-z0-9]/g, casing: "upper" },
  // Setup codes: any printable ASCII except space, uppercased.
  code: { allowed: /[^\x21-\x7e]/g, casing: "upper" },
};

const applyInputFilter = (input) => {
  const filter = INPUT_FILTERS[input.dataset.inputFilter];
  if (!filter) return;

  const before = input.value;
  let after = before.replace(filter.allowed, "");
  after = filter.casing === "lower" ? after.toLowerCase() : after.toUpperCase();
  if (after === before) return;

  // Preserve the caret: rewriting value would otherwise jump it to the end on
  // every keystroke that gets filtered.
  const caret = input.selectionStart - (before.length - after.length);
  input.value = after;
  if (input.setSelectionRange) input.setSelectionRange(caret, caret);
};

/* ------------------------------------------------------ currency suggestion */

// Step 3's currency select follows the country until the visitor picks a
// currency themselves. `data-currency-suggestions` holds {"ALPHA3": "CODE"}
// pairs, `data-currency-us-suggestion` the code for a US organization, and
// `data-currency-country-field` / `data-currency-non-us-field` the ids of the
// controls that drive it. The rules live server-side
// (Organization#signup_currency_suggestion); this replays them as the controls
// change. With the non-US box unchecked the country is hidden and the
// organization is US; with it checked the country decides, and a country
// outside the table clears the select rather than leaving a stale pick.
const suggestCurrency = (select) => {
  if (select.dataset.userChosen === "true") return;

  const suggestions = parseMaybeJson(select.dataset.currencySuggestions) || {};
  const country = document.getElementById(select.dataset.currencyCountryField);
  const nonUs = document.getElementById(select.dataset.currencyNonUsField);

  const suggested =
    nonUs && !nonUs.checked ? select.dataset.currencyUsSuggestion : suggestions[country ? country.value : ""];
  const offered = suggested && select.querySelector(`option[value="${CSS.escape(suggested)}"]`);
  select.value = offered ? suggested : "";
};

const currencySelectsDrivenBy = (control) =>
  Array.from(document.querySelectorAll("[data-currency-suggestions]")).filter(
    (select) => select.dataset.currencyCountryField === control.id || select.dataset.currencyNonUsField === control.id,
  );

/* --------------------------------------------------------- duplicate fields */

// Step 2 renders `league_name` twice, in two mutually exclusive branches of the
// organization-type reveal. Both carry the same input name, so whichever sits
// last in the DOM is what actually submits -- regardless of which one the
// visitor could see and type into. Keeping them in step means the submitted
// value is the one that was filled in.
const syncDuplicateFields = (input) => {
  document.querySelectorAll(`.js-league-name[name="${CSS.escape(input.name)}"]`).forEach((other) => {
    if (other !== input) other.value = input.value;
  });
};

/* --------------------------------------------------------- submit guarding */

// `data-disable-with` on a submit button, normally supplied by rails-ujs. This
// surface deliberately loads no UJS (it competes with htmx for form events), so
// the behavior is reimplemented here.

// This is not cosmetic: a double-submitted signup provisions the organization
// twice, and the request is slow enough for an impatient second click to be
// likely rather than theoretical.
const guardSubmit = (form) => {
  form.querySelectorAll("[data-disable-with]").forEach((button) => {
    if (button.disabled) return;

    // Disabling immediately would drop the button from the submitted payload,
    // so defer past the current event loop turn.
    setTimeout(() => {
      button.disabled = true;
      // Stash the label before overwriting it. It is the only copy -- the
      // original text lives nowhere else once it is replaced -- and the
      // back/forward restore below needs it to put the button back.
      if (button.tagName === "INPUT") {
        button.dataset.enableWith = button.value;
        button.value = button.dataset.disableWith;
      } else {
        button.dataset.enableWith = button.textContent;
        button.textContent = button.dataset.disableWith;
      }
    }, 0);
  });
};

/* -------------------------------------------- back/forward cache restoration */

// The back button can restore this page from the browser's back/forward cache
// with the DOM exactly as it was left. A step that was submitted therefore comes
// back with its submit button still disabled and still reading "Saving…", and
// with any optional color input that was dropped from that payload still
// disabled. `DOMContentLoaded` does not fire on a bfcache restore, so neither
// state is ever undone and the visitor's only way forward is a reload, which
// discards everything they typed.
//
// Deliberately leaves `[data-disable-when-hidden]` alone. Those are disabled as
// a function of whether they sit inside a concealed container, which bfcache
// restores accurately -- re-enabling one would put a concealed `required`
// control back into the form, which blocks submission with no visible field to
// attach the message to.
//
// Not gated on `event.persisted`: nothing carries a stash on a freshly rendered
// step, so the selector below matches nothing and this is a no-op there. Running
// it either way also covers a browser that restores control state without
// flagging the navigation as persisted -- the stash is part of the restored DOM,
// so it is a better signal than `persisted` for exactly that case.
const restoreGuardedControls = () => {
  // The stash is the record that `guardSubmit` is what disabled this button, so
  // it scopes the restore as well as supplying the label. A button disabled by
  // anything else -- a view that renders one disabled until some precondition is
  // met -- is left alone rather than silently enabled on every page load.
  document.querySelectorAll("[data-disable-with][data-enable-with]").forEach((button) => {
    button.disabled = false;

    if (button.tagName === "INPUT") {
      button.value = button.dataset.enableWith;
    } else {
      button.textContent = button.dataset.enableWith;
    }
    delete button.dataset.enableWith;
  });
};

/* -------------------------------------------------------------- delegation */

// Provisioning progress on the final step. The account worker publishes status
// over Pusher; each event carries the step number it has reached, and the
// checklist derives every row's state from that.
//
// Opted into by `data-provisioning` on the list's container, which also carries
// the channel, the Pusher credentials and the translated status words -- all
// server-side values, so the alternative was an inline script in the view.
//
// A row is finished once step_num reaches its `data-done-at`, and active if it is
// the first unfinished one. DERIVING the whole list on every event, rather than
// mutating the row the event names, is what makes an out-of-order or repeated
// event harmless: the list can only ever show a state the step number describes.
// Deriving is not the same as writing, though -- see the equality check below.
const renderProvisioning = (rows, labels, stepNum) => {
  let firstUnfinished = true;

  rows.forEach((row) => {
    const doneAt = parseInt(row.dataset.doneAt, 10);
    let state;

    if (stepNum >= doneAt) {
      state = "done";
    } else if (firstUnfinished) {
      state = "active";
      firstUnfinished = false;
    } else {
      state = "pending";
    }

    // Only write a row that actually changed. Each row is an atomic live region,
    // so rewriting an unchanged one re-announces it -- and this runs on every
    // event, which would mean announcing the whole list each time.
    if (row.dataset.state === state) return;

    row.dataset.state = state;
    row.querySelector(".signup-progress-status").textContent = labels[state];
  });
};

const initProvisioning = () => {
  const container = document.querySelector("[data-provisioning]");
  // Absent on every signup page but one, and on that page once setup completes.
  if (!container || typeof Pusher === "undefined") return;

  const rows = Array.from(container.querySelectorAll(".signup-progress li"));
  const labels = JSON.parse(container.dataset.provisioningLabels);
  // `hold` is a dev-only affordance that keeps this view on screen after the
  // work finishes; see Signup::BaseAccountsController#provisioning_preview.
  const hold = container.dataset.provisioningHold === "true";

  const cluster = container.dataset.provisioningCluster;
  const pusher = new Pusher(container.dataset.provisioningKey, cluster ? { cluster } : {});

  pusher.subscribe(container.dataset.provisioningChannel).bind("status", (data) => {
    if (typeof data.step_num === "number") renderProvisioning(rows, labels, data.step_num);

    if (data.state === "completed") {
      if (!hold) window.location.reload();
    } else if (data.state === "error") {
      document.getElementById("error-instructions").hidden = false;
      container.hidden = true;
    }
  });
};

document.addEventListener("click", (event) => {
  const closeButton = event.target.closest("[data-toast-close]");
  if (closeButton) {
    dismissToast(closeButton.closest("[data-toast]"));
    return;
  }

  const disclosure = event.target.closest("[data-disclosure-for]");
  if (disclosure) {
    event.preventDefault();
    toggleDisclosure(disclosure);
  }
});

document.addEventListener("input", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.matches("[data-input-filter]")) applyInputFilter(target);
  if (target.matches(".js-league-name")) syncDuplicateFields(target);
});

document.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.matches("[data-mirror-fields]")) applyFieldMirror(target);

  // The visitor's own pick ends the suggestions; a change to a driving control
  // updates every select it drives.
  if (target.matches("[data-currency-suggestions]")) target.dataset.userChosen = "true";
  currencySelectsDrivenBy(target).forEach(suggestCurrency);

  // A radio group's change event fires on the newly checked input, but the
  // others in the group need re-evaluating too, since their selectors may
  // overlap.
  if (target.type === "radio" && target.name) {
    document
      .querySelectorAll(`[data-toggle-selector][type="radio"][name="${CSS.escape(target.name)}"]`)
      .forEach(updateDisplayToggle);
  } else if (target.matches("[data-toggle-selector]")) {
    updateDisplayToggle(target);
  }
});

document.addEventListener("submit", (event) => {
  if (!(event.target instanceof HTMLFormElement)) return;

  guardSubmit(event.target);
});

document.addEventListener("DOMContentLoaded", () => {
  initToasts();
  initDisplayToggles();
  initProvisioning();
});

// On `window`, not `document` -- `pageshow` is a window event. It fires on every
// load as well as on a back/forward restore, and is deliberately not gated on
// `event.persisted`: a freshly rendered step has nothing disabled, so this is a
// no-op there, and running it either way also covers a browser that restores
// control state without flagging the navigation as persisted.
window.addEventListener("pageshow", restoreGuardedControls);
